import { useEffect, useState } from "react";
import { BsChevronLeft } from "react-icons/bs";
import { MdArrowOutward } from "react-icons/md";
import MeritoButton from "../../../../../components/common/MeritoButtons/MeritoButtons";
import TextCopy from "../../../../../components/common/TextCopy";
import type { ConnectionHubError, MailRule } from "../../../api/connectionHub.types";
import type { ProviderSetupGuide } from "../../../constants/providerSetupGuides.constants";
import ConnectionErrorPanel from "../../common/ConnectionErrorPanel/ConnectionErrorPanel";
import CopyLinkButton from "../../common/CopyLinkButton/CopyLinkButton";
import ProfileConfirmPanel from "../../common/ProfileConfirmPanel/ProfileConfirmPanel";
import MailRuleFields from "../OutlookRulesPanel/MailRuleFields";
import StepsAccordion from "../StepsAccordion/StepsAccordion";
import styles from "./RecheckPanel.module.scss";

/**
 * After a failed verification only. Forwarding and the Outlook rule start on "Open … Settings", which
 * goes straight to the choice; the filter step opens on the choice. Nothing is opened until the user
 * picks "Update …": "I've Set Up …" keeps the setup as it is (onSkip), while Update names the mailbox
 * (updateProfile), opens its settings on "Yes, Continue" and ends in the normal confirm (updated).
 */
export type RecheckStep = "start" | "choice" | "updateProfile" | "updated";

/** forwarding/filter are Gmail's two checks; rule is Outlook's single one. */
export type RecheckPhase = "forwarding" | "filter" | "rule";

export interface RecheckPanelProps {
  phase: RecheckPhase;
  providerName: string;
  accountEmail: string;
  forwardingAddress?: string | null;
  settingsUrl: string | null;
  guide?: ProviderSetupGuide;
  rules?: MailRule[];
  error?: ConnectionHubError | string | null;
  canOpen?: boolean;
  canCopy?: boolean;
  canConfirm?: boolean;
  disabled?: boolean;
  busy?: boolean;
  openDirectly?: boolean;
  onOpen: () => void;
  /** Choice "I've Set Up …": the setup is still in place, so this step is skipped. */
  onSkip?: () => void;
  /** Normal confirm after "Update …". */
  onConfirmUpdated?: () => void;
}

const COPY: Record<RecheckPhase, {
  openLabel: string;
  existingLabel: string;
  updateLabel: string;
  updatedLabel: string;
  notUpdatedLabel: string;
}> = {
  forwarding: {
    openLabel: "Open Forwarding Settings",
    existingLabel: "I’ve Set Up Forwarding",
    updateLabel: "Update Forwarding",
    updatedLabel: "I’ve Set Up Forwarding",
    notUpdatedLabel: "I’ve Not Set Up Forwarding",
  },
  filter: {
    openLabel: "Open Filter Settings",
    existingLabel: "I’ve Set Up Filter",
    updateLabel: "Update Filter",
    updatedLabel: "I’ve Created Filter",
    notUpdatedLabel: "I’ve Not Created Filter",
  },
  rule: {
    openLabel: "Open Outlook Rules",
    existingLabel: "I’ve Set Up Rules",
    updateLabel: "Update Rules",
    updatedLabel: "I’ve Set Up Rules",
    notUpdatedLabel: "I’ve Not Set Up Rules",
  },
};

const RecheckPanel = ({
  phase,
  providerName,
  accountEmail,
  forwardingAddress,
  settingsUrl,
  guide,
  rules = [],
  error,
  canOpen = true,
  canCopy = true,
  canConfirm = true,
  disabled = false,
  busy = false,
  openDirectly = false,
  onOpen,
  onSkip,
  onConfirmUpdated,
}: RecheckPanelProps): JSX.Element => {
  const firstStep: RecheckStep = phase === "filter" ? "choice" : "start";
  const [step, setStep] = useState<RecheckStep>(firstStep);
  const copy = COPY[phase];

  // A new phase (forwarding → filter) starts from its first screen.
  useEffect(() => { setStep(firstStep); }, [firstStep]);

  const open = (next: RecheckStep): void => {
    onOpen();
    setStep(next);
  };

  const sentence = phase === "forwarding" ? (
    <p className={styles.sentence}>
      <span>Check if forwarding to</span>
      {forwardingAddress ? <TextCopy iconOnlyFeedback text={forwardingAddress} ariaLabel="Copy Merito forwarding address" className={styles.chip} /> : null}
      <span className={styles.regular}>is still set up in this inbox&apos;s settings.</span>
    </p>
  ) : phase === "filter" ? (
    <p className={styles.sentence}>Confirm the combined filter covering every channel on this inbox still exists.</p>
  ) : (
    <p className={styles.sentence}>
      {rules.length > 1
        ? `Confirm the ${rules.length} rules covering these channels still exist in Outlook.`
        : "Confirm the rule covering these channels still exists in Outlook."}
    </p>
  );

  // Outlook can't prefill a rule, so the update path shows exactly what to re-create.
  const ruleDetails = phase === "rule" && rules.length ? (
    <div className={styles.rules}>
      {rules.map((rule, index) => (
        <div className={styles.rule} key={rule.name}>
          <span className={styles.ruleLabel}>{rules.length > 1 ? `Rule ${index + 1}` : "Rule"}</span>
          <MailRuleFields rule={rule} />
        </div>
      ))}
    </div>
  ) : null;

  const confirmPanel = (next: RecheckStep): JSX.Element => (
    <ProfileConfirmPanel
      providerName={providerName}
      accountEmail={accountEmail}
      note="Make sure you're signed into this account in your browser. If not, copy the link and paste it where that account is signed in."
      onDismiss={() => setStep("choice")}
    >
      <MeritoButton
        type="secondary-2"
        size="md"
        className={styles.action}
        btnText="Yes, Continue"
        icon={<MdArrowOutward aria-hidden="true" />}
        isIconToRight
        disabled={!settingsUrl || disabled}
        onClick={() => open(next)}
      />
      {canCopy ? <CopyLinkButton value={settingsUrl} disabled={disabled} className={styles.action} /> : null}
    </ProfileConfirmPanel>
  );

  return (
    <div className={styles.panel}>
      {error ? <ConnectionErrorPanel error={error} /> : sentence}

      {step === "choice" && phase === "filter" && guide ? (
        <StepsAccordion steps={guide.filter} videoUrl={guide.filterVideoUrl} phase="filter" providerName={guide.displayName} forwardingAddress={forwardingAddress} />
      ) : null}

      {step === "start" ? (
        <div className={styles.actions}>
          <MeritoButton
            type="secondary-2"
            size="md"
            className={styles.action}
            btnText={copy.openLabel}
            disabled={disabled}
            onClick={() => (openDirectly ? onOpen() : setStep("choice"))}
          />
        </div>
      ) : null}

      {step === "updateProfile" ? confirmPanel("updated") : null}

      {step === "choice" ? (
        <div className={styles.row}>
          <div className={styles.actions}>
            <MeritoButton
              type="secondary-2"
              size="md"
              className={styles.action}
              btnText={copy.existingLabel}
              loading={busy}
              showLoadingWithText
              disabled={disabled || !canConfirm}
              onClick={onSkip}
            />
            <MeritoButton
              type="tertiary"
              size="md"
              className={styles.action}
              btnText={copy.updateLabel}
              disabled={disabled || busy || !canOpen || !settingsUrl}
              onClick={() => setStep("updateProfile")}
            />
          </div>
          {firstStep === "start" ? (
            <MeritoButton
              type="tertiary"
              size="md"
              className={styles.action}
              btnText="Go Back"
              icon={<BsChevronLeft aria-hidden="true" />}
              disabled={disabled || busy}
              onClick={() => setStep("start")}
            />
          ) : null}
        </div>
      ) : null}

      {step === "updated" ? (
        <>
          {ruleDetails}
          <div className={styles.actions}>
            <MeritoButton
              type="secondary-2"
              size="md"
              className={styles.action}
              btnText={copy.updatedLabel}
              loading={busy}
              showLoadingWithText
              disabled={disabled || !canConfirm}
              onClick={onConfirmUpdated}
            />
            <MeritoButton
              type="tertiary"
              size="md"
              className={styles.action}
              btnText={copy.notUpdatedLabel}
              disabled={disabled || busy}
              onClick={() => setStep("choice")}
            />
          </div>
        </>
      ) : null}
    </div>
  );
};

export default RecheckPanel;
